import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { OpenTabContextProvider } from './context_providers/file_open_tab';
import {
  LocalFilesContextProvider,
  LocalFilesProvider,
} from './context_providers/file_local_search';
import {
  AIContextCategory,
  AIContextItem,
  AIContextProvider,
  AIContextProviderType,
  AIContextSearchQuery,
} from '.';

const AIContextMapping: Record<AIContextCategory, AIContextProviderType[]> = {
  file: ['open_tab', 'local_file_search'],
  snippet: ['snippet'],
  issue: ['issue'],
  merge_request: ['merge_request'],
};

export interface AIContextManager extends DefaultAIContextManager {}

export const AIContextManager = createInterfaceId<AIContextManager>('AIContextManager');

@Injectable(AIContextManager, [OpenTabContextProvider, LocalFilesProvider])
export class DefaultAIContextManager implements AIContextManager {
  #providers: AIContextProvider<AIContextItem>[] = [];

  constructor(
    openTabProvider: OpenTabContextProvider,
    localFilesProvider: LocalFilesContextProvider,
  ) {
    this.#providers.push(
      openTabProvider as unknown as AIContextProvider<AIContextItem>,
      localFilesProvider as unknown as AIContextProvider<AIContextItem>,
    );
  }

  #getProviderAndPerform(
    subType: AIContextProviderType | undefined,
    callback: (provider: AIContextProvider<AIContextItem>) => void,
  ): void {
    const e = new Error('No provider found for type');
    const provider = this.#providers.find((p) => p.type === subType);
    if (!provider) {
      throw e;
    }
    callback(provider);
  }

  getProviders() {
    return this.#providers;
  }

  addSelectedContextItem(contextItem: AIContextItem): void {
    this.#getProviderAndPerform(contextItem.metadata?.subType, (provider) => {
      provider.addSelectedContextItem(contextItem);
    });
  }

  removeSelectedContextItem(contextItem: AIContextItem): void {
    this.#getProviderAndPerform(contextItem.metadata?.subType, (provider) => {
      provider.removeSelectedContextItem(contextItem.id);
    });
  }

  getSelectedContextItems(): AIContextItem[] {
    return this.#providers.flatMap((provider) => provider.getSelectedContextItems());
  }

  async searchContextItemsForCategory(query: AIContextSearchQuery): Promise<AIContextItem[]> {
    const contextItems: AIContextItem[] = [];
    const typesForCategory = AIContextMapping[query.category];
    const providers = this.#providers.filter((p) => typesForCategory.includes(p.type));

    const contextItemsPromises = providers.map(async (provider) => {
      const contextItemsForProvider = await provider.searchContextItems(query);
      return contextItemsForProvider;
    });

    const contextItemsForAllProviders = await Promise.all(contextItemsPromises);
    contextItemsForAllProviders.forEach((items) => contextItems.push(...items));

    // TODO: add params to enable/disable the logic below
    // https://gitlab.com/gitlab-org/gitlab/-/issues/489466
    const selectedContextItemsSet = new Set(this.getSelectedContextItems().map((item) => item.id));
    // Filter out the selected context items from the context items
    const nonSelectedItems = contextItems.filter((item) => !selectedContextItemsSet.has(item.id));

    return this.#disabledContextItemsToTheBack(nonSelectedItems);
  }

  #disabledContextItemsToTheBack(contextItems: AIContextItem[]) {
    return contextItems.sort((a, b) => {
      if (a.metadata?.enabled === b.metadata?.enabled) return 0;
      return a.metadata?.enabled ? -1 : 1;
    });
  }

  getAvailableCategories(): AIContextCategory[] {
    const types = this.#providers.map((provider) => provider.type);
    const categories = Object.keys(AIContextMapping) as AIContextCategory[];
    return types
      .map((type) => categories.find((category) => AIContextMapping[category].includes(type)))
      .filter((category) => category !== undefined);
  }

  async retrieveSelectedContextItemsWithContent(): Promise<AIContextItem[]> {
    const providersWithContext = await Promise.all(
      this.#providers.map((provider) => provider.retrieveSelectedContextItemsWithContent()),
    );
    return providersWithContext.flat();
  }

  clearSelectedContextItems(): void {
    this.#providers.forEach((provider) => provider.clearSelectedContextItems());
  }
}
